// CustomerDialog.tsx
// Reusable Customer Dialog Component for Creating/Editing Customers

import React, { useState } from 'react';
import { adminService, CustomerAddress } from '../../services/adminService';
import { CUSTOMER_TYPE_OPTIONS, CustomerTypeCode, hasOrganizationName, normalizeCustomerType, organizationNameLabel } from '../../utils/customerType';
import '../../styles/CustomerDialog.css';

// Customer interface for the dialog
export interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  landlinePhone?: string; // T-100 (v1.8.24): Festnetz, freiwillig
  companyName?: string;
  customerType?: string; // T-099 (v1.8.24): Private, Business, Club, PublicAuthority
  isVip: boolean;
  addresses?: CustomerAddress[];
}

interface CustomerDialogProps {
  onClose: () => void;
  onSave: (customer: Customer) => void;
  customer: Customer | null; // null = new customer
  // T-141 B (v1.8.43): Startwerte für einen NEUEN Kunden (aus einer Terminanfrage); bei vorhandenem Kunden ohne Wirkung
  vorbelegung?: { firstName?: string; lastName?: string; email?: string; phone?: string };
}

interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  landlinePhone: string; // T-100
  companyName: string;
  customerType: CustomerTypeCode; // T-099
  isVip: boolean;
  addresses?: CustomerAddress[];
  // T-070 (v1.8.25): Rechnungsanschrift direkt im Dialog (Straße, PLZ, Ort), freiwillig.
  // Bisher hatte der Dialog "Neuer Kunde" im Buchungsablauf keine Adressfelder, die Rechnung blieb ohne Anschrift.
  street: string;
  postalCode: string;
  city: string;
}

// T-070 (v1.8.25): Adresse für die Vorbelegung wählen (Rechnungsadresse, sonst Standard, sonst erste)
const pickAddress = (addresses?: CustomerAddress[]): CustomerAddress | undefined => {
  if (!addresses || addresses.length === 0) return undefined;
  // CustomerSelector liefert das Feld "type", die Kundenverwaltung "addressType"
  const norm = (a: CustomerAddress) => (((a as any).type || a.addressType || '') as string).toLowerCase();
  return addresses.find(a => norm(a) === 'billing')
    || addresses.find(a => a.isDefault)
    || addresses[0];
};

export const CustomerDialog: React.FC<CustomerDialogProps> = ({
  onClose,
  onSave,
  customer,
  vorbelegung
}) => {
  const neu = customer ? undefined : vorbelegung;
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: customer?.firstName || neu?.firstName || '',
    lastName: customer?.lastName || neu?.lastName || '',
    email: customer?.email || neu?.email || '',
    phone: customer?.phone || neu?.phone || '',
    landlinePhone: customer?.landlinePhone || '',
    companyName: customer?.companyName || '',
    customerType: normalizeCustomerType(customer?.customerType || (customer?.companyName ? 'business' : 'private')),
    isVip: customer?.isVip || false,
    addresses: customer?.addresses || [],
    // T-070: vorhandene Adresse vorbelegen (Rechnungsadresse zuerst, sonst Standard, sonst erste)
    street: pickAddress(customer?.addresses)?.street || '',
    postalCode: pickAddress(customer?.addresses)?.postalCode || '',
    city: pickAddress(customer?.addresses)?.city || ''
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    // T-070 (v1.8.25): Adresse freiwillig, aber wenn ein Feld belegt ist, dann alle drei; PLZ 4 bis 5 Ziffern
    const street = formData.street.trim();
    const postalCode = formData.postalCode.trim();
    const city = formData.city.trim();
    const hasAnyAddress = !!(street || postalCode || city);
    if (hasAnyAddress && (!street || !postalCode || !city)) {
      setError('Bitte Straße, PLZ und Ort vollständig angeben (oder alle drei Felder leer lassen).');
      setSaving(false);
      return;
    }
    if (hasAnyAddress && !/^[0-9]{4,5}$/.test(postalCode)) {
      setError('Bitte eine gültige Postleitzahl angeben (4 bis 5 Ziffern).');
      setSaving(false);
      return;
    }

    try {
      let savedCustomer: any;

      // Prepare customer data (T-070: plus Adresse, sofern angegeben)
      const customerData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        landlinePhone: formData.landlinePhone || '', // T-100 (v1.8.24)
        companyName: hasOrganizationName(formData.customerType) ? (formData.companyName || '') : '',
        customerType: formData.customerType, // T-099 (v1.8.24)
        isVip: formData.isVip
      };

      // T-070: Adresse als Rechnungsadresse (Typ Billing) plus flache Felder (AdminCustomersController
      // verarbeitet beides). Beim Bearbeiten nur mitsenden, wenn sich die Adresse geändert hat oder der
      // Kunde noch keine hat; sonst bleibt die Adresspflege in der Kundenverwaltung.
      const existing = pickAddress(customer?.addresses);
      const addressChanged = !existing
        || existing.street !== street || existing.postalCode !== postalCode || existing.city !== city;
      if (hasAnyAddress && (!customer || addressChanged)) {
        customerData.street = street;
        customerData.postalCode = postalCode;
        customerData.city = city;
        customerData.country = 'Deutschland';
        if (!customer || !customer.addresses || customer.addresses.length === 0) {
          customerData.addresses = [{
            type: 'Billing',
            street,
            postalCode,
            city,
            country: 'Deutschland',
            isDefault: true
          }];
        } else {
          customerData.addresses = customer.addresses.map(a => {
            const isPicked = a === existing;
            return {
              type: ((a as any).type || a.addressType || 'Private'),
              street: isPicked ? street : a.street,
              postalCode: isPicked ? postalCode : a.postalCode,
              city: isPicked ? city : a.city,
              country: a.country || 'Deutschland',
              isDefault: a.isDefault
            };
          });
        }
      }

      if (customer) {
        // Update existing customer
        savedCustomer = await adminService.updateCustomer(customer.id, customerData);
      } else {
        // Create new customer
        savedCustomer = await adminService.createCustomer(customerData);
      }

      // Transform response to Customer format for consistency
      const transformedCustomer: Customer = {
        id: savedCustomer.id || savedCustomer.Id,
        firstName: savedCustomer.firstName || savedCustomer.FirstName,
        lastName: savedCustomer.lastName || savedCustomer.LastName,
        email: savedCustomer.email || savedCustomer.Email,
        phone: savedCustomer.phone || savedCustomer.Phone,
        landlinePhone: savedCustomer.landlinePhone || savedCustomer.LandlinePhone || '',
        companyName: savedCustomer.companyName || savedCustomer.CompanyName,
        customerType: savedCustomer.customerType || savedCustomer.CustomerType || formData.customerType,
        isVip: savedCustomer.isVip || savedCustomer.IsVip || false,
        addresses: savedCustomer.addresses || savedCustomer.Addresses || []
      };

      onSave(transformedCustomer);
    } catch (err: any) {
      console.error('Error saving customer:', err);
      setError(err.message || 'Fehler beim Speichern des Kunden');
      setSaving(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{customer ? 'Kunde bearbeiten' : 'Neuer Kunde'}</h2>
          <button className="close-btn" onClick={onClose} type="button">
            &times;
          </button>
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="customer-form">
          <div className="form-row">
            <div className="form-group">
              <label>Vorname *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Nachname *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={saving}
            />
          </div>

          {/* T-100 (v1.8.24): in der Verwaltung sind Handynummer und Festnetz freiwillig (Kundin 23.09.2026) */}
          <div className="form-row">
            <div className="form-group">
              <label>Handynummer</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Festnetz</label>
              <input
                type="tel"
                value={formData.landlinePhone}
                onChange={(e) => setFormData({ ...formData, landlinePhone: e.target.value })}
                disabled={saving}
              />
            </div>
          </div>

          {/* T-099 (v1.8.24): Kundentyp mit Namensfeld für Firma, Verein oder Behörde */}
          <div className="form-group">
            <label>Kundentyp</label>
            <select
              value={formData.customerType}
              onChange={(e) => setFormData({ ...formData, customerType: e.target.value as CustomerTypeCode })}
              disabled={saving}
            >
              {CUSTOMER_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {hasOrganizationName(formData.customerType) && (
            <div className="form-group">
              <label>{organizationNameLabel(formData.customerType)}</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                disabled={saving}
              />
            </div>
          )}

          {/* T-070 (v1.8.25): Rechnungsanschrift, freiwillig; kommt auf Anzahlungs- und Schlussrechnung */}
          <div className="form-group">
            <label>Straße und Hausnummer</label>
            <input
              type="text"
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              disabled={saving}
              placeholder="z. B. Lichtenfelser Weg 26"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>PLZ</label>
              <input
                type="text"
                inputMode="numeric"
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label>Ort</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.isVip}
                onChange={(e) => setFormData({ ...formData, isVip: e.target.checked })}
                disabled={saving}
              />
              VIP-Kunde
            </label>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={onClose} disabled={saving} className="btn-cancel">
              Abbrechen
            </button>
            <button type="submit" disabled={saving} className="btn-submit">
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
